import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { parseLaravelPaginator } from '../../lib/parseLaravelPaginator';
import { normalizeUser } from '../auth/normalizeUser';

/**
 * Real-backend Audit Logs surface — GET /admin/audit-logs, gated by the
 * super-admin ROLE specifically (not a manage-* permission like every other
 * admin-tagged endpoint so far) — a plain admin gets a straight 403.
 * Read-only, no mutations, so no tagTypes/invalidation needed (same as
 * dashboardApi/reportsApi). Superseded the old mock-era stub that pointed at
 * axiosBaseQuery (the DEMO_MODE mock server) with no response shaping —
 * this is the second half of the app now fully wired to the live API.
 *
 * Response is a bare Laravel paginator (current_page/data/last_page/... at
 * the top level, no `meta` nesting) — parseLaravelPaginator already
 * tolerates that exact shape as its fallback path.
 */

// The docs' example entry has no `id`/`uuid` field on a log row at all —
// spatie/laravel-activitylog rows do have a real db id, but since it's not
// documented as present, this defensively falls back to a caller-supplied
// per-page-position key so React list rendering never breaks even if the
// field really is missing.
function normalizeCauser(raw) {
  if (!raw) return null;
  return normalizeUser(raw);
}

function normalizeAuditEntry(raw, fallbackKey) {
  if (!raw) return raw;
  return {
    id: raw.id ?? raw.uuid ?? fallbackKey,
    logName: raw.log_name ?? null,
    description: raw.description ?? '',
    event: raw.event ?? null,
    subjectType: raw.subject_type ?? null,
    subjectId: raw.subject_id ?? null,
    causer: normalizeCauser(raw.causer),
    // Always an object, never undefined — {} renders as "no extra detail"
    // rather than the row's detail section needing its own null check.
    properties: raw.properties && typeof raw.properties === 'object' ? raw.properties : {},
    createdAt: raw.created_at ?? null,
  };
}

export const auditApi = createApi({
  reducerPath: 'auditApi',
  baseQuery: authBaseQuery(),
  tagTypes: [],
  endpoints: (builder) => ({
    // GET /admin/audit-logs?from=&to=&log_name=&event=&causer_uuid=&search=&per_page=&page=
    // Every param optional/combinable — callers pass `undefined` for any
    // filter that's not active, which axios drops from the query string.
    listAuditLogs: builder.query({
      query: ({ from, to, log_name, event, causer_uuid, search, per_page, page } = {}) => ({
        url: '/admin/audit-logs',
        method: 'GET',
        params: { from, to, log_name, event, causer_uuid, search, per_page, page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'auditApi:list' });
        return {
          entries: items.map((raw, i) => normalizeAuditEntry(raw, `row-${i}`)),
          meta,
          parseError: !!parseError,
        };
      },
    }),
  }),
});

export const { useListAuditLogsQuery } = auditApi;
